import { useEffect, useState, useRef } from 'react';
import Swal from 'sweetalert2';

const SessionTimeoutNotification = () => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [alertShown, setAlertShown] = useState(false);
  const alertRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // Listen for token expiration warnings
    const handleTokenExpiring = (event) => {
      const expiresIn = event.detail.expiresIn;

      // Show warning when less than 2 minutes (120 seconds)
      if (expiresIn <= 120 && !alertShown) {
        setTimeRemaining(expiresIn);
        showSweetAlert(expiresIn);
      }
    };

    window.addEventListener('tokenExpiringSoon', handleTokenExpiring);

    return () => {
      window.removeEventListener('tokenExpiringSoon', handleTokenExpiring);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [alertShown]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const showSweetAlert = (initialTime) => {
    if (alertShown) return;

    setAlertShown(true);
    let remainingTime = initialTime;

    // Fire the SweetAlert
    alertRef.current = Swal.fire({
      title: '<strong>Session Expiring Soon!</strong>',
      icon: 'warning',
      html: `
        <div style="text-align: left; padding: 10px;">
          <p style="margin-bottom: 15px; color: #666;">
            Your session will expire due to inactivity. Any unsaved changes may be lost.
          </p>
          <div style="background: linear-gradient(135deg, #f59e0b 0%, #ea580c 100%); 
                      color: white; 
                      padding: 15px; 
                      border-radius: 12px; 
                      margin: 15px 0;
                      box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
            <div style="display: flex; align-items: center; justify-content: center; gap: 10px;">
              <svg style="width: 24px; height: 24px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" stroke-width="2"/>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6l4 2"/>
              </svg>
              <span id="countdown-timer" style="font-size: 28px; font-weight: bold; font-family: monospace;">
                ${formatTime(remainingTime)}
              </span>
              <span style="font-size: 14px; opacity: 0.9;">remaining</span>
            </div>
          </div>
          <div style="background: #fef3c7; 
                      border-left: 4px solid #f59e0b; 
                      padding: 12px; 
                      border-radius: 8px;
                      margin-top: 15px;">
            <p style="margin: 0; font-size: 13px; color: #92400e;">
              💡 <strong>Tip:</strong> Continue working to stay logged in. Your session will refresh automatically.
            </p>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Stay Logged In',
      cancelButtonText: 'Dismiss',
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#6b7280',
      allowOutsideClick: false,
      allowEscapeKey: true,
      customClass: {
        popup: 'session-timeout-alert',
        confirmButton: 'swal-confirm-btn',
        cancelButton: 'swal-cancel-btn'
      },
      didOpen: () => {
        // Start countdown timer
        const timerElement = document.getElementById('countdown-timer');

        timerRef.current = setInterval(() => {
          remainingTime--;
          setTimeRemaining(remainingTime);

          if (timerElement) {
            timerElement.textContent = formatTime(remainingTime);
          }

          // Auto-close when time runs out
          if (remainingTime <= 0) {
            clearInterval(timerRef.current);
            Swal.close();
          }
        }, 1000);
      },
      willClose: () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }
      }
    }).then((result) => {
      setAlertShown(false);

      if (result.isConfirmed) {
        // User clicked "Stay Logged In" - trigger an API call to refresh token
        // This will happen automatically via axios interceptor when user interacts
        Swal.fire({
          title: 'Session Extended!',
          text: 'Your session has been refreshed. Continue working.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }
    });
  };

  // No visual component needed - SweetAlert handles the UI
  return null;
};

export default SessionTimeoutNotification;
